import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { api } from '../client'
import { chaves as chavesDaSessao } from './chaves'
import type { NovoUsuario, Papel, Pendencia, UsuarioAdministrado } from '../../types/usuario'

/**
 * As chaves de cache da administração de contas.
 *
 * Prefixo comum `['admin', 'usuarios']`: toda mutação derruba a lista inteira, porque conceder
 * papel a uma pessoa muda o que outra vê na mesma tela (o contador de administradores, por
 * exemplo). Cache fino aqui economizaria uma requisição e custaria uma tela desatualizada sobre
 * quem pode aprovar mudança.
 */
const chaves = {
  tudo: () => ['admin', 'usuarios'] as const,
  lista: (busca: string) => ['admin', 'usuarios', 'lista', busca] as const,
}

/**
 * As contas cadastradas.
 *
 * @param busca trecho do nome ou do e-mail
 * @returns a consulta
 */
export function useUsuarios(busca: string) {
  return useQuery({
    queryKey: chaves.lista(busca),
    queryFn: async () => {
      const { data } = await api.get<UsuarioAdministrado[]>('/admin/usuarios', {
        params: busca ? { busca } : undefined,
      })
      return data
    },
  })
}

/** Cadastra uma conta com senha provisória. */
export function useCriarUsuario() {
  const cliente = useQueryClient()
  return useMutation({
    mutationFn: async (novo: NovoUsuario) => {
      const { data } = await api.post<UsuarioAdministrado>('/admin/usuarios', novo)
      return data
    },
    onSuccess: () => void cliente.invalidateQueries({ queryKey: chaves.tudo() }),
  })
}

/** Troca o conjunto completo de papéis de uma conta. */
export function useDefinirPapeis(id: number) {
  const cliente = useQueryClient()
  return useMutation({
    mutationFn: async (papeis: Papel[]) => {
      const { data } = await api.put<UsuarioAdministrado>(`/admin/usuarios/${id}/papeis`, { papeis })
      return data
    },
    onSuccess: () => void cliente.invalidateQueries({ queryKey: chaves.tudo() }),
  })
}

/** Redefine a senha, obrigando a troca no próximo acesso. */
export function useRedefinirSenha(id: number) {
  const cliente = useQueryClient()
  return useMutation({
    mutationFn: async (senhaProvisoria: string) => {
      await api.post(`/admin/usuarios/${id}/senha`, { senhaProvisoria })
    },
    onSuccess: () => void cliente.invalidateQueries({ queryKey: chaves.tudo() }),
  })
}

/** Ativa ou desativa o acesso de uma conta. */
export function useDefinirAtivo(id: number) {
  const cliente = useQueryClient()
  return useMutation({
    mutationFn: async (ativo: boolean) => {
      const { data } = await api.patch<UsuarioAdministrado>(`/admin/usuarios/${id}/ativo`, { ativo })
      return data
    },
    onSuccess: () => void cliente.invalidateQueries({ queryKey: chaves.tudo() }),
  })
}

/** A própria pessoa troca a senha. Exige a atual — a sessão aberta sozinha não basta. */
export function useTrocarPropriaSenha() {
  const cliente = useQueryClient()
  return useMutation({
    mutationFn: async (dados: { senhaAtual: string; novaSenha: string }) => {
      await api.post('/me/senha', dados)
    },
    // O `deveTrocarSenha` vem de `GET /me`: sem invalidar a sessão, a tela continuaria exigindo a
    // troca que acabou de acontecer.
    onSuccess: () => void cliente.invalidateQueries({ queryKey: chavesDaSessao.eu() }),
  })
}

/**
 * A própria pessoa corrige o nome e o e-mail. A senha atual só é exigida quando o e-mail muda.
 *
 * Invalida a sessão pelo mesmo motivo da troca de senha: nome e e-mail vêm de `GET /me`, e o
 * cabeçalho continuaria mostrando o nome antigo.
 */
export function useAlterarDadosPessoais() {
  const cliente = useQueryClient()
  return useMutation({
    mutationFn: async (dados: { nome: string; email: string; senhaAtual?: string }) => {
      await api.put('/me/dados', dados)
    },
    onSuccess: () => void cliente.invalidateQueries({ queryKey: chavesDaSessao.eu() }),
  })
}

/**
 * O que está esperando o usuário logado.
 *
 * `staleTime` curto: a pessoa resolve uma pendência e volta para o painel esperando o número ter
 * mudado. Cache longo aqui faria o painel afirmar trabalho que já foi feito.
 *
 * @returns a consulta das pendências
 */
export function useMinhasPendencias() {
  return useQuery({
    queryKey: ['me', 'pendencias'],
    queryFn: async () => (await api.get<Pendencia[]>('/me/pendencias')).data,
    staleTime: 15 * 1000,
  })
}
